import {
  AfterViewChecked,
  Component,
  EventEmitter, HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { LandmarkPanel } from '@form/lm-form.model';
import { LMDashboardViewComponent } from '@dashboard/lm-dashboard-view.component';
import { Observable, of as observableOf } from 'rxjs';
import { LMSpecService } from '@services/lm-spec.service';
import { LMListTransactionEvent, LMRefreshRequest } from '@app/components';
import { LMConditionUtilService } from '@app/utils';
import {DomSanitizer, SafeStyle} from "@angular/platform-browser";

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector:    '[lm-page-pane-dashboard-component]',
  templateUrl: './lm-page-pane-dashboard.component.html',
  styleUrls:   [ 'lm-page-pane-dashboard.component.scss' ],

})
export class LMPagePaneDashBoardComponent implements OnInit, AfterViewChecked {
  @ViewChild('dashboardViewComponent', { static: true }) dashboardViewComponent: LMDashboardViewComponent;

  @HostBinding('style.height') get hostHeight(){
    return this.hasBackgroundImage ? '100%' : 'inherit';
  };

  @Input() columns: 3 | 4 = 3;

  @Input() pane: LMMultiPagePaneSpec;

  @Input() set fromDataView(fromDataView: LMDataView) {
    this.m_fromDataView = fromDataView;
  }

  @Input() set messageRuleView(messageRuleView: LMDataView) {
    this.m_messageRuleView = messageRuleView;
  }

  get messageRuleView(): LMDataView {
    return this.m_messageRuleView;
  }

  @Output() contentModified = new EventEmitter<any>();
  @Output() panelContentDisplayed = new EventEmitter<LMContentDisplayedEvent>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() panelDataUpdated = new EventEmitter<any>();

  public m_fromDataView: LMDataView;
  private m_messageRuleView: LMDataView;
  public panels: Array<LandmarkPanel>;
  private refreshPending: boolean;


  public hasBackgroundImage = false;
  public backgroundImageUrl: SafeStyle;

  constructor(
    public lmSpecService: LMSpecService,
    private lmConditionService: LMConditionUtilService,
    private _sanitizer: DomSanitizer) {
  }

  ngOnInit() {
    this.transformPanesToLandmarkPanels();
    this.buildPageBackground();
  }

  ngAfterViewChecked() {
    if (this.refreshPending) {
      this.refreshPending = false;
      setTimeout(() => this.transformPanesToLandmarkPanels());
    }
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onDashboardContentDisplayed() {
    const event: LMContentDisplayedEvent = { pane: this.pane, args: null };
    this.panelContentDisplayed.emit(event);
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onPanelDataUpdated(event: LMListTransactionEvent) {
    this.panelDataUpdated.emit(event);
  }

  sendBusinessContext(contextMsgs?: any) {
    // stub this out to avoid a js error
    // TODO jerry or cheryl can you implement if needed
    // got here because lm-page-panel.component.getPagePaneListComponents is
    // a list of any objects.
  }

  public isModified(): boolean {
    if (this.dashboardViewComponent) {
      return this.dashboardViewComponent.isModified();
    }

    return false;
  }

  public refresh(refreshRequest?: LMRefreshRequest): Observable<boolean> {
    this.refreshPending = true;

    if (this.dashboardViewComponent) {
      return this.dashboardViewComponent.refresh(refreshRequest);
    }

    return observableOf(true);
  }

  public save(): Observable<boolean> {
    if (this.dashboardViewComponent && this.dashboardViewComponent.isModified()) {
      return this.dashboardViewComponent.runSaveAction();
    }

    return observableOf(false); // safety
  }

  private transformPanesToLandmarkPanels() {

    if (this.pane.panes) {
      this.panels = [];
      const useTwelveColumnGrid = this.pane.useTwelveColumnGrid;
      for (let i = 0, len = this.pane.panes.length; i < len; i++) {
        const pane: LMPagePaneSpec = this.pane.panes[ i ];
        let isVisible = true;
        let isValid = true;

        if (this.m_messageRuleView) {
          if (pane.visibility) {
            if (!this.lmConditionService.isValid([ pane.visibility ], this.m_messageRuleView)) {
              isVisible = false;
            }
          }
          if (pane.validity) {
            if (!this.lmConditionService.isValid([ pane.validity ], this.m_messageRuleView)) {
              isValid = false;
            }
          }
        }

        const title = this.pane.panes[ i ].paneTitle.resourceValue;

        switch (pane.paneType) {
          case 'list':
            this.panels.push({
              id:                 pane.paneId,
              automationId:       pane.paneAutomationId,
              title,
              content:            '',
              isVisible,
              isValid,
              index:              i,
              initialized:        false,
              loaded:             false,
              reload:             false,
              disabled:           false,
              hidden:             false,
              listSpec:           pane.listSpec,
              type:               pane.paneType,
              overviewNavigation: pane.overviewNavigation,
              displayChart:       pane.displayChart,
              widgetWidth:        useTwelveColumnGrid ? undefined : this.lmSpecService.toWidgetSizeToType(pane.dashboardViewWidth),
              widgetHeight:       useTwelveColumnGrid ? undefined : this.lmSpecService.toWidgetSizeToType(pane.dashboardViewHeight),
              spanningColumns:    useTwelveColumnGrid ? pane.twelveColumnGridSpan : undefined
            });
            break;
          case 'url':
            this.panels.push({
              id:                 pane.paneId,
              automationId:       pane.paneAutomationId,
              title,
              content:            '',
              url:                this.pane.panes[ i ].url,
              isVisible,
              isValid,
              index:              i,
              initialized:        false,
              loaded:             false,
              reload:             false,
              disabled:           false,
              hidden:             false,
              listSpec:           pane.listSpec,
              type:               pane.paneType,
              overviewNavigation: pane.overviewNavigation,
              displayChart:       pane.displayChart,
              widgetWidth:        useTwelveColumnGrid ? undefined : this.lmSpecService.toWidgetSizeToType(pane.dashboardViewWidth),
              widgetHeight:       useTwelveColumnGrid ? undefined : this.lmSpecService.toWidgetSizeToType(pane.dashboardViewHeight),
              spanningColumns:    useTwelveColumnGrid ? pane.twelveColumnGridSpan : undefined
            });
            break;
          case 'menuPanel':
            this.panels.push({
              id:                 pane.paneId,
              automationId:       pane.paneAutomationId,
              title,
              content:            '',
              isVisible,
              isValid,
              index:              i,
              initialized:        false,
              loaded:             false,
              reload:             false,
              disabled:           false,
              hidden:             false,
              type:               pane.paneType,
              layout:             pane.pane.menuPanelLayout,
              overviewNavigation: null,
              widgetWidth:        useTwelveColumnGrid ? undefined : this.lmSpecService.toWidgetSizeToType(pane.dashboardViewWidth),
              widgetHeight:       useTwelveColumnGrid ? undefined : this.lmSpecService.toWidgetSizeToType(pane.dashboardViewHeight),
              spanningColumns:    useTwelveColumnGrid ? pane.twelveColumnGridSpan : undefined
            });
            break;
          default:
            break;
        }
      }
    }
  }

  private buildPageBackground() {
    if (!!this.pane?.background) {
      this.hasBackgroundImage = true;
      this.backgroundImageUrl = this._sanitizer.bypassSecurityTrustStyle(`url('${this.pane.background.image}')`);
    }
  }
}
